import { VideoAppPromoModalEvents } from 'Analytics/video-app-promo-modal-events';
import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import 'Components/molecules/vm-modal/vm-modal';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { isIosOrIpad } from 'Utils/device';
import { getDynamicState, getInterstitialStates, setDynamicState } from 'Utils/dynamic-state';
import { isDesktop } from 'Utils/viewport';
import { UserStore } from 'Main/user-store';
import Styles from './vm-video-app-promo-modal.scss';

const isRefresh = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';

const DEFERRED_KEY = 'vm_video_app_promo_modal_deferred';
const DISMISSED_TTL_MS = 24 * 60 * 60 * 1000;
const TRIGGER_DELAY_MS = 5000;
const DYNAMIC_STATE_KEY = 'vm-video-app-promo-modal';
const VIDEO_APP_PROMO_QUERY_PARAM = 'video-app-promo-clicked';

// Mirror R1: a refresh resets the dismiss cooldown so R2 can show again
// (on the next page if R1 also shows, or on this page if R1 is inactive).
if (isRefresh) {
    UserStore.removeVideoAppPromoDismissedAt();
}

function isDismissed(): boolean {
    const dismissedAt = UserStore.loadVideoAppPromoDismissedAt();
    return dismissedAt !== null && Date.now() - dismissedAt < DISMISSED_TTL_MS;
}

function r1ShownOnThisPage(): boolean {
    const shownAt = UserStore.loadAppGateShownAt();
    return shownAt !== null && shownAt >= performance.timeOrigin;
}

@customElement('vm-video-app-promo-modal')
export class VmVideoAppPromoModal extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'app-store-url' })
    appStoreUrl = '';

    @property({ type: String, attribute: 'qr-code-src' })
    qrCodeSrc = '';

    @property({ type: String, attribute: 'vumedi-logo-src' })
    vumediLogoSrc = '';

    @property({ type: String, attribute: 'app-store-badge-src' })
    appStoreBadgeSrc = '';

    @property({ type: Boolean, attribute: 'mobile-enabled' })
    mobileEnabled = false;

    @property({ type: Boolean, attribute: 'desktop-enabled' })
    desktopEnabled = false;

    @state()
    private _rendered = false;

    @state()
    private _open = false;

    @state()
    private _variant: 'mobile' | 'desktop' | null = null;

    private _stateChangeHandler: (() => void) | null = null;

    override connectedCallback() {
        super.connectedCallback();
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this._initialize(), { once: true });
        } else {
            this._initialize();
        }
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        if (this._stateChangeHandler) {
            window.removeEventListener('dynamicstatechange', this._stateChangeHandler);
            this._stateChangeHandler = null;
        }
    }

    private _initialize() {
        if (isIosOrIpad() && this.hasAttribute('mobile-enabled')) {
            this._variant = 'mobile';
        } else if (isDesktop() && this.hasAttribute('desktop-enabled')) {
            this._variant = 'desktop';
        } else {
            setDynamicState(DYNAMIC_STATE_KEY, false);
            return;
        }

        // A prior page deferred R2 due to a conflicting interstitial — bypass own
        // dismiss TTL on this page, but still respect R1 and only on non-refresh navigations.
        let isDeferred = false;
        if (!isRefresh) {
            try {
                if (sessionStorage.getItem(DEFERRED_KEY) === '1') {
                    isDeferred = true;
                    sessionStorage.removeItem(DEFERRED_KEY);
                }
            } catch {}
        }

        if (
            (!isDeferred && !isRefresh && isDismissed()) ||
            (window.__VUMEDI_CONTEXT__?.appGateActive && r1ShownOnThisPage())
        ) {
            setDynamicState(DYNAMIC_STATE_KEY, false);
            return;
        }

        // Static ISI tray and floating ISI preview are server-rendered and always in the
        // DOM at this point — defer immediately rather than waiting for TRIGGER_DELAY_MS.
        if (this._hasStaticIsi()) {
            this._deferToNextPage();
            return;
        }

        setTimeout(() => this._maybeOpen(), TRIGGER_DELAY_MS);
    }

    private _maybeOpen() {
        // Track whether any interstitial became active while we waited. Once any modal
        // has shown on this page we defer to the next page rather than stacking prompts.
        let _hadConflict = false;

        const isQueueClear = () => {
            const interstitialStates = getInterstitialStates();
            const chatbotTooltipState = getDynamicState('chatbot-tooltip');
            // undefined means setup-chatbot hasn't resolved yet — treat as pending
            if (chatbotTooltipState === undefined) return false;
            const allData = [...interstitialStates.map((s) => s.data), chatbotTooltipState];
            if (allData.some((s) => s === true)) _hadConflict = true;
            return allData.every((s) => s !== true);
        };

        if (isQueueClear()) {
            this._openModal();
            return;
        }

        this._stateChangeHandler = () => {
            if (!isQueueClear()) return;
            window.removeEventListener('dynamicstatechange', this._stateChangeHandler!);
            this._stateChangeHandler = null;
            if (_hadConflict) {
                this._deferToNextPage();
            } else {
                this._openModal();
            }
        };
        window.addEventListener('dynamicstatechange', this._stateChangeHandler);
    }

    private _hasStaticIsi(): boolean {
        return !!document.querySelector('#video-safety-information-box');
    }

    private _deferToNextPage() {
        try {
            sessionStorage.setItem(DEFERRED_KEY, '1');
        } catch {}
        setDynamicState(DYNAMIC_STATE_KEY, false);
    }

    private get _ctaUrl(): string {
        return `${this.appStoreUrl}?p=${this._variant}`;
    }

    private get _eventProps() {
        return { interstitial_type: 'video_modal', platform: this._variant, page_url: window.location.href };
    }

    private _openModal() {
        try {
            sessionStorage.removeItem(DEFERRED_KEY);
        } catch {}
        this._rendered = true;
        setDynamicState(DYNAMIC_STATE_KEY, true);
        window.vumedi?.mixpanel?.track(VideoAppPromoModalEvents.INTERSTITIAL_OPENED, this._eventProps);
        if (this._variant === 'desktop') {
            // Defer so the panel is in the DOM before the CSS transition fires.
            requestAnimationFrame(() => {
                this._open = true;
            });
        } else {
            this._open = true;
        }
    }

    private _dismiss() {
        UserStore.saveVideoAppPromoDismissedAt(Date.now());
        setDynamicState(DYNAMIC_STATE_KEY, false);
        window.vumedi?.mixpanel?.track(VideoAppPromoModalEvents.DISMISSED, this._eventProps);
    }

    private _handleVmClose() {
        this._open = false;
        this._dismiss();
    }

    private _handleDesktopClose() {
        this._open = false;
        this._dismiss();
    }

    private async _handleCta(event: Event) {
        event.preventDefault();
        UserStore.saveVideoAppPromoDismissedAt(Date.now());
        await window.vumedi?.mixpanel?.track(VideoAppPromoModalEvents.CTA_CLICKED, this._eventProps);
        if (this._variant === 'mobile') {
            // Navigate to the current video page (www-stripped) so iOS Universal Links open
            // the video in the app. If not installed, middleware catches the query param and
            // redirects to the App Store.
            const url = new URL(window.location.href);
            url.searchParams.set(VIDEO_APP_PROMO_QUERY_PARAM, '');
            this._navigate(url.href.replace('https://www.', 'https://'));
        } else {
            this._navigate(this._ctaUrl);
        }
    }

    protected _navigate(url: string) {
        window.location.href = url;
    }

    private get _mobileActions() {
        return html`
            <vm-button variant="solid" color-scheme="primary" size="lg" class="vpm__cta" @click=${this._handleCta}>
                Open Vumedi
            </vm-button>
            <vm-button
                variant="unstyled"
                color-scheme="neutral"
                size="lg"
                full-width
                @click=${() => {
                    this._open = false;
                }}
                >Not now</vm-button
            >
        `;
    }

    private get _renderDesktop() {
        return html`
            <aside
                class=${classMap({ vpm__panel: true, 'vpm__panel--open': this._open })}
                role="dialog"
                aria-label="Download the Vumedi app"
            >
                <vm-button
                    class="vpm__panel-close"
                    variant="ghost"
                    color-scheme="neutral"
                    icon-only
                    size="sm"
                    aria-label="Dismiss"
                    @click=${this._handleDesktopClose}
                >
                    <vm-icon-next slot="icon-leading" name="x-close" size="sm"></vm-icon-next>
                </vm-button>

                <div class="vpm__panel-header">
                    <div class="vpm__app-icon" aria-hidden="true">
                        <img src=${this.vumediLogoSrc} alt="" width="40" height="40" />
                    </div>
                    <div class="vpm__panel-heading">
                        <p class="vpm__panel-app-name">Vumedi</p>
                        <p class="vpm__panel-tagline">Medical Video Education</p>
                        <div class="vpm__panel-rating" aria-label="4.9 stars, 3.7 thousand ratings">
                            <span class="vpm__panel-stars" aria-hidden="true">
                                ${[1, 2, 3, 4, 5].map(() => html`<vm-icon-next name="star" size="xs"></vm-icon-next>`)}
                            </span>
                            <span class="vpm__panel-rating-value">4.9</span>
                            <span aria-hidden="true">·</span>
                            <span>3.7k ratings</span>
                        </div>
                    </div>
                </div>

                <div class="vpm__panel-body">
                    <h2 class="vpm__panel-headline">Take Vumedi with you.</h2>
                    <p class="vpm__panel-value-prop">
                        Watch and listen in the Vumedi app on your iPhone, whether you’re between patients, on rounds,
                        commuting, or learning on the go.
                    </p>
                </div>

                <div class="vpm__panel-qr-row">
                    <a
                        class="vpm__panel-qr"
                        href=${this._ctaUrl}
                        @click=${this._handleCta}
                        aria-label="Download on the App Store"
                    >
                        <img src=${this.qrCodeSrc} alt="QR code to download the Vumedi app" width="80" height="80" />
                    </a>
                    <div class="vpm__panel-qr-text">
                        <p class="vpm__panel-qr-label">Scan with your iPhone</p>
                        <p class="vpm__panel-qr-sub">
                            Point your camera at the code to download Vumedi on the App Store.
                        </p>
                    </div>
                </div>

                <div class="vpm__panel-divider"><span>or</span></div>

                <div class="vpm__panel-badge-row">
                    <a class="vpm__panel-badge" href=${this._ctaUrl} @click=${this._handleCta}>
                        ${this.appStoreBadgeSrc
                            ? html`<img src=${this.appStoreBadgeSrc} alt="Download on the App Store" />`
                            : html`<span class="vpm__panel-badge-text">Download on the App Store</span>`}
                    </a>
                </div>
            </aside>
        `;
    }

    override render() {
        if (!this._rendered) return nothing;

        if (this._variant === 'desktop') {
            return this._renderDesktop;
        }

        return html`
            <vm-modal ?open=${this._open} @vm-close=${this._handleVmClose}>
                <div class="vpm__content">
                    <div class="vpm__medallion">
                        <img src=${this.vumediLogoSrc} alt="Vumedi logo" width="44" height="44" />
                    </div>
                    <h2 class="vpm__headline">Get the full app experience</h2>
                    <p class="vpm__subhead">Watch or Listen to 100,000+ great lectures on the go.</p>
                    <div class="vpm__actions">${this._mobileActions}</div>
                </div>
            </vm-modal>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-app-promo-modal': VmVideoAppPromoModal;
    }
}
